import { useMemo, useState } from 'react'
import { Link, Navigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, Card, CardContent, CardHeader, CardTitle, Input } from '@agms/ui'
import {
  pricingTemplateAdminApi,
  type ComplianceProgram,
  type PricingOption,
  type PricingTemplate,
  type PricingTemplatePayload,
} from '../lib/api'
import { usePermissions } from '../hooks/useAuth'
import { apiErrorMessage, apiValidationErrors } from '../hooks/useErrorHandler'
import { CardTitle as SectionCardTitle, PageHeader, SurfaceCard } from '../components/ui'

const emptyPayload: PricingTemplatePayload = {
  name: '',
  pricing_option: 'A',
  bankcard_discount_rate: '',
  bankcard_per_item: '',
  debit_discount_rate: '',
  debit_per_item: '',
  amex_rate: '',
  amex_per_item: '',
  discover_rate: '',
  discover_per_item: '',
  auth_fee: '',
  flat_rate_bankcard: '',
  flat_rate_debit: '',
  flat_rate_amex: '',
  flat_rate_discover: '',
  compliance_program: 'none',
  compliance_fee_pct: '',
  compliance_fee_amt: '',
  batch_fee: '',
  maintenance_fee: '',
  pin_debit_item_rate: '',
  pin_debit_volume_rate: '',
}

const optionLabels: Record<PricingOption, string> = {
  A: 'A — Interchange Plus',
  B: 'B — Flat Rate',
}

function ratesDiffer(...values: Array<string | number | null | undefined>): boolean {
  const filled = values
    .filter((value) => value !== null && value !== undefined && value !== '')
    .map((value) => String(value))

  return new Set(filled).size > 1
}

function valueForForm(value: PricingTemplate[keyof PricingTemplate] | undefined): string {
  if (value === null || value === undefined) return ''
  return String(value)
}

function toPayload(form: HTMLFormElement): PricingTemplatePayload {
  const data = new FormData(form)
  const field = (name: keyof PricingTemplatePayload) => {
    const value = String(data.get(name) ?? '').trim()
    return value === '' ? null : value
  }
  const pricingOption = String(data.get('pricing_option') ?? 'A') as PricingOption
  const separateCardRates = data.get('separate_card_rates') === 'on'
  const pinDebitEnabled = data.get('pin_debit_enabled') === 'on'
  const bankcardDiscount = field('bankcard_discount_rate')
  const flatBankcard = field('flat_rate_bankcard')

  return {
    name: String(data.get('name') ?? '').trim(),
    pricing_option: pricingOption,
    bankcard_discount_rate: pricingOption === 'A' ? bankcardDiscount : null,
    bankcard_per_item: null,
    debit_discount_rate: pricingOption === 'A' ? (separateCardRates ? field('debit_discount_rate') : bankcardDiscount) : null,
    debit_per_item: null,
    amex_rate: pricingOption === 'A' ? (separateCardRates ? field('amex_rate') : bankcardDiscount) : null,
    amex_per_item: null,
    discover_rate: pricingOption === 'A' ? (separateCardRates ? field('discover_rate') : bankcardDiscount) : null,
    discover_per_item: null,
    auth_fee: field('auth_fee'),
    flat_rate_bankcard: pricingOption === 'B' ? flatBankcard : null,
    flat_rate_debit: pricingOption === 'B' ? (separateCardRates ? field('flat_rate_debit') : flatBankcard) : null,
    flat_rate_amex: pricingOption === 'B' ? (separateCardRates ? field('flat_rate_amex') : flatBankcard) : null,
    flat_rate_discover: pricingOption === 'B' ? (separateCardRates ? field('flat_rate_discover') : flatBankcard) : null,
    compliance_program: String(data.get('compliance_program') ?? 'none') as ComplianceProgram,
    compliance_fee_pct: field('compliance_fee_pct'),
    compliance_fee_amt: field('compliance_fee_amt'),
    batch_fee: field('batch_fee'),
    maintenance_fee: field('maintenance_fee'),
    pin_debit_item_rate: pinDebitEnabled ? field('pin_debit_item_rate') : null,
    pin_debit_volume_rate: pinDebitEnabled ? field('pin_debit_volume_rate') : null,
  }
}

function formatRate(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—'
  const parsed = Number(value)
  if (Number.isNaN(parsed)) return String(value)
  return parsed.toFixed(4)
}

function statusClass(active: boolean): string {
  return active ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-600'
}

function TextField({ name, label, template }: { name: keyof PricingTemplatePayload; label: string; template?: PricingTemplate | null }) {
  return (
    <Input
      name={name}
      label={label}
      type="number"
      step="0.0001"
      min="0"
      defaultValue={template ? valueForForm(template[name]) : valueForForm(emptyPayload[name])}
    />
  )
}

function ConfirmModal({
  title,
  body,
  confirmLabel,
  onCancel,
  onConfirm,
  isPending,
}: {
  title: string
  body: string
  confirmLabel: string
  onCancel: () => void
  onConfirm: () => void
  isPending?: boolean
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 px-4">
      <div className="w-full max-w-md rounded-lg bg-white shadow-xl">
        <div className="border-b border-gray-200 px-5 py-4">
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
        </div>
        <div className="px-5 py-4">
          <p className="text-sm text-gray-700">{body}</p>
        </div>
        <div className="flex justify-end gap-2 border-t border-gray-200 px-5 py-4">
          <Button variant="secondary" autoFocus onClick={onCancel}>Cancel</Button>
          <Button onClick={onConfirm} disabled={isPending}>
            {isPending ? 'Working...' : confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  )
}

function PricingTemplateForm({
  template,
  onCancel,
  onSubmit,
  isPending,
  error,
}: {
  template: PricingTemplate | null
  onCancel: () => void
  onSubmit: (payload: PricingTemplatePayload) => void
  isPending: boolean
  error: string | null
}) {
  const [pricingOption, setPricingOption] = useState<PricingOption>(template?.pricing_option ?? 'A')
  const [complianceProgram, setComplianceProgram] = useState<ComplianceProgram>((template?.compliance_program as ComplianceProgram) ?? 'none')
  const [editRatesSeparately, setEditRatesSeparately] = useState(() => {
    if (!template) return false
    return template.pricing_option === 'B'
      ? ratesDiffer(template.flat_rate_bankcard, template.flat_rate_debit, template.flat_rate_amex, template.flat_rate_discover)
      : ratesDiffer(template.bankcard_discount_rate, template.debit_discount_rate, template.amex_rate, template.discover_rate)
  })
  const [pinDebitEnabled, setPinDebitEnabled] = useState(Boolean(template?.pin_debit_item_rate || template?.pin_debit_volume_rate))

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    onSubmit(toPayload(event.currentTarget))
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{template ? 'Edit Template' : 'New Template'}</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid gap-4 md:grid-cols-2">
            <Input name="name" label="Name" required maxLength={100} defaultValue={template?.name ?? ''} />
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Pricing Option</label>
              <div className="flex flex-wrap gap-3">
                {(['A', 'B'] as PricingOption[]).map((option) => (
                  <label key={option} className="flex items-center gap-2 rounded-md border border-gray-200 px-3 py-2 text-sm">
                    <input
                      type="radio"
                      name="pricing_option"
                      value={option}
                      checked={pricingOption === option}
                      onChange={() => setPricingOption(option)}
                    />
                    {optionLabels[option]}
                  </label>
                ))}
              </div>
            </div>
          </div>

          {pricingOption === 'A' ? (
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-gray-900">Interchange Plus Rates</h3>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  name="separate_card_rates"
                  checked={editRatesSeparately}
                  onChange={(event) => setEditRatesSeparately(event.target.checked)}
                />
                Edit card rates separately
              </label>
              <div className="grid gap-3 md:grid-cols-4">
                <TextField name="bankcard_discount_rate" label={editRatesSeparately ? 'Bankcard Discount' : 'Visa / MC / Debit / Amex / Discover Discount'} template={template} />
                {editRatesSeparately && (
                  <>
                    <TextField name="debit_discount_rate" label="Debit Discount" template={template} />
                    <TextField name="amex_rate" label="Amex Rate" template={template} />
                    <TextField name="discover_rate" label="Discover Rate" template={template} />
                  </>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-gray-900">Flat Rates</h3>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  name="separate_card_rates"
                  checked={editRatesSeparately}
                  onChange={(event) => setEditRatesSeparately(event.target.checked)}
                />
                Edit card rates separately
              </label>
              <div className="grid gap-3 md:grid-cols-4">
                <TextField name="flat_rate_bankcard" label={editRatesSeparately ? 'Bankcard' : 'Visa / MC / Debit / Amex / Discover'} template={template} />
                {editRatesSeparately && (
                  <>
                    <TextField name="flat_rate_debit" label="Debit" template={template} />
                    <TextField name="flat_rate_amex" label="Amex" template={template} />
                    <TextField name="flat_rate_discover" label="Discover" template={template} />
                  </>
                )}
              </div>
            </div>
          )}

          <div className="grid gap-3 md:grid-cols-3">
            <TextField name="auth_fee" label="Auth Fee" template={template} />
          </div>

          {pricingOption === 'A' && (
            <div className="space-y-3">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <input
                  type="checkbox"
                  name="pin_debit_enabled"
                  checked={pinDebitEnabled}
                  onChange={(event) => setPinDebitEnabled(event.target.checked)}
                />
                Add PIN Debit rates
              </label>
              {pinDebitEnabled && (
                <div className="grid gap-3 md:grid-cols-3">
                  <TextField name="pin_debit_item_rate" label="PIN Debit Item Rate" template={template} />
                  <TextField name="pin_debit_volume_rate" label="PIN Debit Volume Rate" template={template} />
                </div>
              )}
            </div>
          )}

          <div className="grid gap-3 md:grid-cols-2">
            <TextField name="batch_fee" label="Batch Fee" template={template} />
            <Input
              name="maintenance_fee"
              label="Maintenance Fee"
              type="number"
              step="0.01"
              min="0"
              defaultValue={template ? valueForForm(template.maintenance_fee) : ''}
            />
          </div>

          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-gray-900">Compliance</h3>
            <div className="grid gap-3 md:grid-cols-3">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Program</label>
                <select
                  name="compliance_program"
                  value={complianceProgram ?? 'none'}
                  onChange={(event) => setComplianceProgram(event.target.value as ComplianceProgram)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                >
                  <option value="none">None</option>
                  <option value="cash_discount">Cash Discount</option>
                  <option value="dual_pricing">Dual Pricing</option>
                </select>
              </div>
              {complianceProgram !== 'none' && (
                <>
                  <TextField name="compliance_fee_pct" label="Compliance Fee %" template={template} />
                  <Input
                    name="compliance_fee_amt"
                    label="Compliance Fee Amount"
                    type="number"
                    step="0.01"
                    min="0"
                    defaultValue={template ? valueForForm(template.compliance_fee_amt) : ''}
                  />
                </>
              )}
            </div>
          </div>

          {error && (
            <p className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
              {error}
            </p>
          )}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={onCancel}>Cancel</Button>
            <Button type="submit" disabled={isPending}>{isPending ? 'Saving...' : 'Save Template'}</Button>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}

export function SettingsPricingTemplatesPage() {
  const { canManagePricingTemplates } = usePermissions()
  const queryClient = useQueryClient()
  const [showArchived, setShowArchived] = useState(false)
  const [editing, setEditing] = useState<PricingTemplate | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const [archiveTarget, setArchiveTarget] = useState<PricingTemplate | null>(null)
  const [defaultTarget, setDefaultTarget] = useState<PricingTemplate | null>(null)

  const { data: templates = [], isLoading, error } = useQuery({
    queryKey: ['pricing-templates-admin'],
    queryFn: pricingTemplateAdminApi.list,
    enabled: canManagePricingTemplates(),
  })

  const currentDefault = templates.find((template) => template.is_default)

  const visibleTemplates = useMemo(() => (
    showArchived ? templates : templates.filter((template) => template.is_active)
  ), [showArchived, templates])

  const saveMutation = useMutation({
    mutationFn: (payload: PricingTemplatePayload) => (
      editing
        ? pricingTemplateAdminApi.update(editing.id, payload)
        : pricingTemplateAdminApi.create(payload)
    ),
    onSuccess: async () => {
      setShowForm(false)
      setEditing(null)
      setFormError(null)
      await queryClient.invalidateQueries({ queryKey: ['pricing-templates-admin'] })
      await queryClient.invalidateQueries({ queryKey: ['pricingTemplates'] })
    },
    onError: (err) => {
      const errors = apiValidationErrors(err)
      const firstError = errors ? Object.values(errors)[0] : null
      setFormError(Array.isArray(firstError) ? firstError[0] : apiErrorMessage(err, 'Unable to save template.'))
    },
  })

  const archiveMutation = useMutation({
    mutationFn: (template: PricingTemplate) => (
      template.is_active
        ? pricingTemplateAdminApi.archive(template.id)
        : pricingTemplateAdminApi.restore(template.id)
    ),
    onSuccess: async () => {
      setArchiveTarget(null)
      await queryClient.invalidateQueries({ queryKey: ['pricing-templates-admin'] })
      await queryClient.invalidateQueries({ queryKey: ['pricingTemplates'] })
    },
  })

  const setDefaultMutation = useMutation({
    mutationFn: (template: PricingTemplate) => pricingTemplateAdminApi.setDefault(template.id),
    onSuccess: async () => {
      setDefaultTarget(null)
      await queryClient.invalidateQueries({ queryKey: ['pricing-templates-admin'] })
      await queryClient.invalidateQueries({ queryKey: ['pricingTemplates'] })
    },
  })

  if (!canManagePricingTemplates()) {
    return <Navigate to="/settings" replace />
  }

  const startCreate = () => {
    setEditing(null)
    setFormError(null)
    setShowForm(true)
  }

  const startEdit = (template: PricingTemplate) => {
    setEditing(template)
    setFormError(null)
    setShowForm(true)
  }

  return (
    <div className="space-y-6">
      <div>
        <Link to="/settings" className="text-sm text-blue-600 hover:text-blue-800">Back to settings</Link>
        <PageHeader
          className="mt-2"
          title="Pricing Templates"
          subtitle="Create rate structures, archive retired templates, and choose the default."
          action={<Button onClick={startCreate}>New Template</Button>}
        />
      </div>

      {showForm && (
        <PricingTemplateForm
          template={editing}
          onCancel={() => {
            setShowForm(false)
            setEditing(null)
            setFormError(null)
          }}
          onSubmit={(payload) => saveMutation.mutate(payload)}
          isPending={saveMutation.isPending}
          error={formError}
        />
      )}

      <SurfaceCard>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <SectionCardTitle>Templates</SectionCardTitle>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={showArchived}
                onChange={(event) => setShowArchived(event.target.checked)}
              />
              Show archived
            </label>
          </div>
        <div className="mt-4">
          {error && (
            <p className="mb-4 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
              Failed to load pricing templates.
            </p>
          )}

          <div className="overflow-x-auto rounded-xl border border-slate-200">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-slate-100">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">Name</th>
                  <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">Pricing</th>
                  <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">Key Rates</th>
                  <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">Default</th>
                  <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">Status</th>
                  <th className="px-4 py-2 text-right text-xs font-medium uppercase text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {isLoading ? (
                  <tr>
                    <td className="px-4 py-8 text-center text-gray-500" colSpan={6}>Loading templates...</td>
                  </tr>
                ) : visibleTemplates.length === 0 ? (
                  <tr>
                    <td className="px-4 py-8 text-center text-gray-500" colSpan={6}>No templates match the current filter.</td>
                  </tr>
                ) : visibleTemplates.map((template) => (
                  <tr key={template.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{template.name}</div>
                      {template.compliance_program && template.compliance_program !== 'none' && (
                        <div className="text-xs capitalize text-gray-500">{template.compliance_program.replace('_', ' ')}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-700">{optionLabels[template.pricing_option]}</td>
                    <td className="px-4 py-3 text-xs text-gray-600">
                      {template.pricing_option === 'A' ? (
                        <div>
                          <div>Bankcard discount: {formatRate(template.bankcard_discount_rate)}</div>
                          <div>Auth fee: {formatRate(template.auth_fee)}</div>
                        </div>
                      ) : (
                        <div>
                          <div>Bankcard flat: {formatRate(template.flat_rate_bankcard)}</div>
                          <div>Debit flat: {formatRate(template.flat_rate_debit)}</div>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {template.is_default ? (
                        <span className="inline-flex rounded-full bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-700">Default</span>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${statusClass(template.is_active)}`}>
                        {template.is_active ? 'Active' : 'Archived'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap justify-end gap-2">
                        <button type="button" onClick={() => startEdit(template)} className="text-sm text-blue-600 hover:text-blue-800">
                          Edit
                        </button>
                        {template.is_active && !template.is_default && (
                          <button type="button" onClick={() => setDefaultTarget(template)} className="text-sm text-emerald-700 hover:text-emerald-900">
                            Set Default
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => setArchiveTarget(template)}
                          className={template.is_active ? 'text-sm text-amber-700 hover:text-amber-900' : 'text-sm text-slate-600 hover:text-slate-900'}
                        >
                          {template.is_active ? 'Archive' : 'Restore'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </SurfaceCard>

      {archiveTarget && (
        <ConfirmModal
          title={`${archiveTarget.is_active ? 'Archive' : 'Restore'} ${archiveTarget.name}?`}
          body={archiveTarget.is_active
            ? 'This template will be hidden from new applications but existing applications using it will not be affected.'
            : 'This template will be available again for new applications.'}
          confirmLabel={archiveTarget.is_active ? 'Archive Template' : 'Restore Template'}
          onCancel={() => setArchiveTarget(null)}
          onConfirm={() => archiveMutation.mutate(archiveTarget)}
          isPending={archiveMutation.isPending}
        />
      )}

      {defaultTarget && (
        <ConfirmModal
          title={`Set ${defaultTarget.name} as default?`}
          body={`This template will be selected by default for new applications. The current default (${currentDefault?.name ?? 'none'}) will no longer be default.`}
          confirmLabel="Set Default"
          onCancel={() => setDefaultTarget(null)}
          onConfirm={() => setDefaultMutation.mutate(defaultTarget)}
          isPending={setDefaultMutation.isPending}
        />
      )}
    </div>
  )
}
